/**
 * Stock Status Badge Component
 * Visual indicator for stock levels with color coding
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { Badge } from '@/components/ui/badge';
import type { StockLevel } from '@/types/availableBatches';
import { getStockLevelColor, getStockLevelLabel } from '@/types/availableBatches';
import { AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StockStatusBadgeProps {
  level: StockLevel;
  availablePercent?: number;
  showIcon?: boolean;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function StockStatusBadge({
  level,
  availablePercent,
  showIcon = true,
  showPercentage = true,
  size = 'md',
  className
}: StockStatusBadgeProps) {
  const colors = getStockLevelColor(level);
  const label = getStockLevelLabel(level);

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  const Icon = {
    CRITICAL: AlertCircle,
    LOW: AlertTriangle,
    NORMAL: CheckCircle
  }[level];

  return (
    <Badge
      className={cn(
        'font-medium border',
        colors.bg,
        colors.text,
        colors.border,
        sizeClasses[size],
        'flex items-center gap-1.5',
        className
      )}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      <span>{label}</span>
      {showPercentage && availablePercent !== undefined && (
        <span className="ml-1 font-semibold">
          ({availablePercent.toFixed(0)}%)
        </span>
      )}
    </Badge>
  );
}
